<script lang="ts">
	import type { FullAutoFill } from 'svelte/elements'
	import { appState } from '$lib/app-state.svelte'

	interface Props {
		id?: string
		type?: string
		value?: string | number
		required?: boolean
		minlength?: number
		maxlength?: number
		class?: string
		label?: string
		autocomplete?: FullAutoFill | null
		invalidity?: string
		input?: HTMLInputElement | HTMLTextAreaElement
		onchange?: (event: Event) => void
		pattern?: string
		rows?: number
	}

	let {
		id,
		type = 'text',
		value = $bindable(),
		input = $bindable(),
		required,
		minlength,
		maxlength = 20,
		class: className,
		label,
		autocomplete,
		invalidity,
		onchange,
		pattern,
		rows = 1
	}: Props = $props()

	let invalidityMessage = $derived(invalidity || `${label} required`)
	let dirty = $state(false)

	const oninput = () => (dirty = true)
</script>

<div class="{className} tw:relative">
	{#if rows > 1}
		<textarea
			{id}
			bind:value
			bind:this={input}
			placeholder=" "
			{required}
			{minlength}
			{maxlength}
			{autocomplete}
			{oninput}
			{onchange}
			{rows}
			class="tw:peer tw:block tw:w-full tw:border tw:border-gray-200 tw:rounded-md tw:px-3 tw:placeholder-transparent tw:text-base tw:leading-5 tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-inset tw:focus:ring-lilac
						tw:pt-6.5 tw:pb-2.5
						tw:peer-placeholder-shown:pt-4 tw:peer-placeholder-shown:pb-4"
			class:tw:invalid:border-red-500={dirty || appState.formInvalid}
			class:tw:invalid:ring-red-500={dirty || appState.formInvalid}></textarea>
	{:else}
		<input
			{id}
			{type}
			bind:value
			bind:this={input}
			placeholder=" "
			{required}
			{minlength}
			{maxlength}
			{autocomplete}
			{oninput}
			{onchange}
			{pattern}
			class="tw:peer tw:block tw:w-full tw:border tw:border-gray-200 tw:rounded-md tw:px-3 tw:placeholder-transparent tw:text-base tw:leading-5 tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-inset tw:focus:ring-lilac
             tw:pt-6.5 tw:pb-2.5 tw:min-h-5
             tw:peer-placeholder-shown:pt-4 tw:peer-placeholder-shown:pb-4 tw:appearance-none"
			class:tw:invalid:border-red-500={dirty || appState.formInvalid}
			class:tw:invalid:ring-red-500={dirty || appState.formInvalid} />
	{/if}

	<label
		for={id}
		class="tw:absolute tw:top-0 tw:left-0 tw:z-10 tw:h-full tw:py-4 tw:px-3 tw:overflow-hidden tw:text-left tw:truncate tw:pointer-events-none tw:border tw:border-transparent tw:origin-[0_0] tw:transition tw:duration-100
				/* Default (transformed) state: when input has a value or is focused */
				tw:scale-[0.85] tw:-translate-y-2 tw:translate-x-0.6 tw:text-plum/80 tw:text-base
				/* When input is empty (placeholder shown), revert to large, centered style */
				tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:translate-y-0 tw:peer-placeholder-shown:translate-x-0 tw:peer-placeholder-shown:text-base tw:peer-placeholder-shown:text-plum
				/* Ensure focus keeps the transformed style */
				tw:peer-focus:scale-[0.85] tw:peer-focus:-translate-y-2 tw:peer-focus:translate-x-0.6 tw:peer-focus:text-plum/80 tw:peer-focus:text-base"
		class:tw:peer-invalid:text-red-500={dirty || appState.formInvalid}>
		{label}
	</label>

	{#if dirty || appState.formInvalid}
		<div class="tw:hidden tw:peer-invalid:block tw:w-full tw:mt-1 tw:text-sm tw:text-red-500">
			{invalidityMessage}
		</div>
	{/if}
</div>
